import moment from 'moment';
import { DATE_TIME_PRIMARY } from '../../constants/date.constants';
import { AssessmentStatus } from './due-date-progress.models';
import { RemediationBaseState } from '../../hocs/with-remediation-base/withRemediationBase.hoc';
import { AssessmentGoalDto } from '../../apis/eols-assessment-service/eols-assessment-service.dtos';

export const getStatus = (props: {
  dueDate: string;
  isComplete: boolean;
  dateCompleted: string;
}): AssessmentStatus => {

  const {
    dueDate,
    isComplete,
    dateCompleted
  } = props;

  if (!dueDate) {
    if (isComplete) {
      return AssessmentStatus.COMPLETED_NOT_DUE;
    }
    return AssessmentStatus.IN_PROGRESS_NOT_DUE;
  }

  if (isComplete) {
    if (dateCompleted && moment(dateCompleted).isAfter(moment(dueDate))) {
      return AssessmentStatus.COMPLETED_LATE;
    }
    return AssessmentStatus.COMPLETED_ON_TIME;
  }
  if (moment().isAfter(dueDate)) {
    return AssessmentStatus.IN_PROGRESS_PAST_DUE;
  }
  return AssessmentStatus.IN_PROGRESS_DUE
}

export const getProgressDisplay = (props: {
  status: AssessmentStatus;
  dateCompleted: string;
  dueDate: string;
}): string => {

  const {
    status,
    dateCompleted,
    dueDate,
  } = props;

  if ([AssessmentStatus.IN_PROGRESS_DUE, AssessmentStatus.IN_PROGRESS_PAST_DUE].includes(status)) {
    return `Due: ${moment(dueDate).format(DATE_TIME_PRIMARY)} ${getDueDisplay(dueDate)}`
  }

  if ([AssessmentStatus.COMPLETED_ON_TIME, AssessmentStatus.COMPLETED_NOT_DUE].includes(status)) {
    if (!dateCompleted) {
      return 'Completed';
    }
    return `Completed: ${moment(dateCompleted).format(DATE_TIME_PRIMARY)}`
  }

  if ([AssessmentStatus.COMPLETED_LATE].includes(status)) {
    if (!dateCompleted) {
      return 'Completed late';
    }
    return `Completed late: ${moment(dateCompleted).format(DATE_TIME_PRIMARY)}`
  }

  return null;
}

export const getDueDisplay = (dueDate: string): string => {
  if (!dueDate) {
    return null;
  }
  // Compare calendar days, not elapsed duration: moment#diff truncates toward zero, so a due
  // date 21 hours out on the next calendar day would otherwise read as 0 days -> "(today)"
  const daysUntil = moment(dueDate).startOf('day').diff(moment().startOf('day'), 'days');

  if (daysUntil === 1) {
    return '(tomorrow)';
  }

  if (daysUntil > 0) {
    return `(in ${daysUntil} days)`;
  }

  if (daysUntil === -1) {
    return '(yesterday)';
  }

  if (daysUntil < 0) {
    return `(${Math.abs(daysUntil)} days ago)`;
  }

  // Same calendar day: distinguish an already-missed deadline from one still ahead, so a
  // past-due student does not read "(today)" as "there is still time"
  if (moment(dueDate).isBefore(moment())) {
    return '(earlier today)';
  }

  return '(today)';

};

export const getAllGoalsAreZero = (baseState: RemediationBaseState): boolean => {
  if (!baseState?.eolsAssessment?.assessmentGoals) {
    return false;
  }

  const goals: AssessmentGoalDto[] = baseState.eolsAssessment.assessmentGoals;

  if (!goals || goals.length === 0) {
    return false;
  }

  return goals.every((goal: AssessmentGoalDto) => goal.goal === 0);
};

